import type { StyleProp } from '@vtex/admin-ui'
import {
  Flex,
  Button,
  Modal,
  ModalContent,
  useModalState,
  Text,
  TextInput,
  csx,
  ModalDismiss,
} from '@vtex/admin-ui'
import type { ChangeEvent } from 'react'
import React, { useRef, useState, useEffect } from 'react'
import { useFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router'

import type { SalesAssociatePasswordModal_salesAssociate$key } from './__generated__/SalesAssociatePasswordModal_salesAssociate.graphql'
import { Avatar } from '../Avatar/Avatar'
import { TextInputPassword } from '../../../Login/components/TextInputPassword/TextInputPassword'

const modalStyles: StyleProp = {
  height: '100vh',
  width: 'full',
  maxHeight: 'full',
  margin: 0,
  flex: 1,
  borderWidth: '0px',
  borderRadius: '0px',
  backgroundColor: '$blue05',
}

const itemsBoxStyles: StyleProp = {
  height: 'full',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
}

export type SalesAssociatePasswordModalProps = {
  salesAssociate: SalesAssociatePasswordModal_salesAssociate$key
  onPasswordSubmit: (matched: boolean) => void
}

export const SalesAssociatePasswordModal = (
  props: SalesAssociatePasswordModalProps
) => {
  const intl = useIntl()
  const modalState = useModalState({ open: true })

  const [value, setValue] = useState('')
  const [showError, setShowError] = useState(false)
  const inputPasswordRef = useRef<HTMLInputElement | null>(null)
  const [typePassword, setTypePassword] = useState(true)
  const history = useHistory()
  const salesAssociateData = useFragment<SalesAssociatePasswordModal_salesAssociate$key>(
    graphql`
      fragment SalesAssociatePasswordModal_salesAssociate on SalesAssociate {
        id
        name
        avatar {
          url
        }
        queueAppPassword
      }
    `,
    props.salesAssociate
  )

  const isPasswordValid = value === salesAssociateData.queueAppPassword

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value

    if (/^\d+$/.test(password) || password === '') {
      setValue(password)
      setShowError(false)
    }
  }

  const handleClick = () => {
    modalState.hide()

    props.onPasswordSubmit(false)
    history.push('/turn-list')
  }

  const handleApplyClick = () => {
    if (!isPasswordValid) {
      setShowError(true)

      return
    }

    props.onPasswordSubmit(isPasswordValid)
    history.push('#/sales-associate-goals')
  }

  useEffect(() => {
    inputPasswordRef.current?.focus()
  }, [])

  return (
    <Modal
      state={modalState}
      aria-label={intl.formatMessage({
        id: 'salesAssociatePasswordModalAriaLabel',
      })}
      className={csx({ ...modalStyles })}
    >
      <ModalContent className={csx({ height: 'full' })}>
        <ModalDismiss
          onClick={handleClick}
          className={csx({ float: 'right' })}
        />

        <Flex className={csx({ ...itemsBoxStyles })}>
          <Avatar src={salesAssociateData?.avatar?.url} size={4.7} />
          <Text
            variant="pageTitle"
            className={csx({ paddingTop: '$space-3', fontWeight: 'bold' })}
          >
            {salesAssociateData?.name}
          </Text>
          <Text variant="title2">
            {intl.messages.salesAssociatePasswordModalPasswordInput as string}
          </Text>
          <div
            className={csx({
              paddingTop: '$space-4',
              width: '18rem',
              '> div > div > div': {
                background: '$white',
              },
            })}
          >
            <TextInput
              className={csx({
                width: 'full',
                color: showError ? 'critical' : 'neutral',
              })}
              name="password"
              typeof="number"
              inputMode="numeric"
              type={typePassword ? 'password' : 'text'}
              value={value}
              onChange={handlePasswordChange}
              id={salesAssociateData.id}
              label={
                intl.messages.salesAssociatePasswordModalPassword as string
              }
              error={showError}
              errorText={
                intl.messages.salesAssociatePasswordModalPasswordError as string
              }
              max={4}
              suffix={
                <TextInputPassword
                  typePassword={typePassword}
                  onClick={() => setTypePassword(!typePassword)}
                />
              }
              ref={inputPasswordRef}
              data-testid="sales-associate-password-modal-input"
            />

            <div
              className={csx({
                paddingTop: '$space-4',
                display: 'flex',
                width: 'full',
              })}
            >
              <Button
                size="large"
                onClick={handleApplyClick}
                className={csx({ width: 'full' })}
                data-testid="sales-associate-password-modal-submit"
              >
                {intl.messages.loginButtonLabel}
              </Button>
            </div>
          </div>
        </Flex>
      </ModalContent>
    </Modal>
  )
}
